// ─── ProductCostEditor.jsx ────────────────────────────────────────────────────
// Per-product cost panel that replaces the deleted Profitability page.
// Lives inline inside each product row in the Marketplaces page.
//
// Each row is one cost line item. Each line item has:
//   - label (free text)
//   - amount (per-unit OR flat fee)
//   - mode: per_unit | flat
//   - deductFromPayout (checkbox: subtract from payout in projections)
//
// Plus the existing typed cost fields are still here:
//   - manufacturing cost (cogs.productCost)
//   - shipping (cogs.shipping) — freight per unit
//   - customs (cogs.customs)
//   - prep (cogs.prep)
//   - ad cost (existing adCost*)
//   - refund cost (existing refund*)
//
// The user can toggle "deductFromPayout" on any of these to make the
// effective payout per unit be `payout - sum(deducted costs)`.

import { useTheme } from "../../lib/ThemeContext.jsx";
import { Info } from "../../components/ui.jsx";
import { FONT, RADIUS, SPRING } from "../../lib/theme.js";
import { currSym } from "../../lib/calculations.js";
import { isOverridden, applyOverride, resetField, getApiValue } from "./marketplaceUtils.js";

const numToInput = (v) => (v == null ? "" : String(v));
const inputToNum = (s) => {
  const t = (s ?? "").trim();
  if (t === "") return null;
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
};

export default function ProductCostEditor({ product, onPatch, onDecouple, onConfirmOverride }) {
  const T = useTheme();
  const cs = currSym();

  // Wraps onPatch with API-override warning logic.
  // If the field has an _apiSnapshot value AND the user is changing it,
  // we let the parent show a confirm dialog first via onConfirmOverride.
  const safePatch = async (field, newVal) => {
    if (getApiValue(product, field) !== undefined && !isOverridden(product, field)) {
      const ok = await onConfirmOverride?.(field);
      if (!ok) return;
    }
    onPatch(applyOverride(product, field, newVal));
  };

  const inputStyle = {
    width: "100%",
    background: "transparent",
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    color: T.text,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    fontFamily: FONT,
    boxSizing: "border-box",
    transition: `border-color 0.18s ${SPRING}`,
  };

  // Reset-to-API button — shown next to fields the user has overridden
  const resetButton = (field) => {
    if (!isOverridden(product, field)) return null;
    const apiVal = getApiValue(product, field);
    if (apiVal === undefined) return null;
    return (
      <button
        onClick={() => onPatch(resetField(product, field))}
        title={`Reset to API value: ${apiVal}`}
        style={{
          marginLeft: 6,
          padding: "2px 8px",
          fontSize: 9, fontWeight: 700,
          background: T.green + "15",
          border: `1px solid ${T.green}40`,
          borderRadius: RADIUS.pill,
          color: T.green,
          fontFamily: FONT,
          cursor: "pointer",
        }}
      >↺ Reset to API</button>
    );
  };

  const fieldLabel = (text, tipKey, field) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
      <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{text}</span>
      {tipKey && <Info tip={tipKey} />}
      {field && resetButton(field)}
    </div>
  );

  // Cogs structure: { productCost, shipping, customs, prep, deductFromPayout: { productCost: true, ... } }
  const cogs = product.cogs || {};
  const cogsDeduct = cogs.deductFromPayout || {};
  const setCogsField = (field, value) => {
    safePatch("cogs", { ...cogs, [field]: value });
  };
  const toggleCogsDeduct = (field) => {
    safePatch("cogs", {
      ...cogs,
      deductFromPayout: { ...cogsDeduct, [field]: !cogsDeduct[field] },
    });
  };

  // Compute the effective payout (payout minus all deducted costs)
  const baseCost = (Number(cogs.productCost) || 0)
    + (Number(cogs.shipping) || 0)
    + (Number(cogs.customs) || 0)
    + (Number(cogs.prep) || 0);

  const deductibles = [
    { key: "productCost", label: "Product cost", value: Number(cogs.productCost) || 0 },
    { key: "shipping",    label: "Shipping",     value: Number(cogs.shipping) || 0 },
    { key: "customs",     label: "Customs",      value: Number(cogs.customs) || 0 },
    { key: "prep",        label: "Prep",         value: Number(cogs.prep) || 0 },
  ];
  const adCost = product.adCostType === "fixed"
    ? (Number(product.adCostAmount) || 0)
    : (Number(product.adCostRate) || 0) / 100 * (Number(product.payout) || 0);
  const refundCost = product.refundType === "fixed"
    ? (Number(product.refundAmount) || 0)
    : (Number(product.refundRate) || 0) / 100 * (Number(product.payout) || 0);

  let effectivePayout = Number(product.payout) || 0;
  for (const d of deductibles) if (cogsDeduct[d.key]) effectivePayout -= d.value;
  if (product.deductAdCostFromProjection) effectivePayout -= adCost;
  if (product.deductRefundFromProjection) effectivePayout -= refundCost;
  const margin = effectivePayout - baseCost;

  return (
    <div style={{
      background: T.bg,
      borderRadius: RADIUS.md,
      padding: 16,
      marginTop: 10,
      border: `1px solid ${T.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: T.accent, textTransform: "uppercase", letterSpacing: 0.5 }}>
          💰 Costs & Profitability
        </div>
        <button
          onClick={() => onDecouple?.(product)}
          title="Decouple this product from all calculations"
          style={{
            padding: "5px 12px",
            fontSize: 10, fontWeight: 700,
            background: product._decoupled ? T.red + "15" : "transparent",
            border: `1px solid ${product._decoupled ? T.red + "50" : T.border}`,
            borderRadius: RADIUS.pill,
            color: product._decoupled ? T.red : T.textSub,
            fontFamily: FONT,
            cursor: "pointer",
          }}
        >
          {product._decoupled ? "🔌 Decoupled — click to recouple" : "🔌 Decouple from calcs"}
        </button>
      </div>

      {product._decoupled && (
        <div style={{
          padding: 10,
          marginBottom: 14,
          background: T.red + "08",
          border: `1px solid ${T.red}30`,
          borderRadius: RADIUS.md,
          fontSize: 11,
          color: T.red,
          lineHeight: 1.5,
        }}>
          <strong>⚠ This product is fully decoupled.</strong> It's excluded from EVERYTHING:
          income projections, inventory pools, stockout calculations, the Dashboard chart,
          and the AI assistant. Reactivate it any time by clicking the decouple button above.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
        {/* Per-cost row */}
        {deductibles.map(d => (
          <div key={d.key}>
            {fieldLabel(d.label, "productCogs", `cogs.${d.key}`)}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: T.textSub }}>{cs}</span>
              <input
                type="number"
                style={inputStyle}
                value={numToInput(cogs[d.key])}
                onChange={e => setCogsField(d.key, inputToNum(e.target.value))}
                placeholder="0"
              />
            </div>
            <label style={{
              display: "flex", alignItems: "center", gap: 5,
              marginTop: 6, cursor: "pointer", fontSize: 10, color: T.textSub,
            }}>
              <input
                type="checkbox"
                checked={!!cogsDeduct[d.key]}
                onChange={() => toggleCogsDeduct(d.key)}
                style={{ cursor: "pointer" }}
              />
              <span>Deduct from payout</span>
            </label>
          </div>
        ))}
      </div>

      {/* Effective payout summary */}
      <div style={{
        marginTop: 14, padding: "12px 16px",
        background: T.surface, borderRadius: RADIUS.md,
        border: `1px solid ${T.border}`,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, marginBottom: 4 }}>Listed payout</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{cs}{(Number(product.payout) || 0).toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, marginBottom: 4 }}>After deductions</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.accent }}>{cs}{effectivePayout.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, marginBottom: 4 }}>Net margin / unit</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: margin >= 0 ? T.green : T.red }}>{cs}{margin.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
